import filter from 'lodash/filter'
import find from 'lodash/find'
import map from 'lodash/map'
import { NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useMemo } from 'react'
import { SWRConfig } from 'swr'

import { contentstackLoader } from '@Components/Image'
import { BlockConfigContext } from '@Globals/CMS/Context'
import { DataLayer } from '@Globals/Google/DataLayer'
import { PageProps } from '@Globals/Page/Types'
import { useTranslation } from '@Globals/Translations'

const DynamicHead = ({
  alternateLinks = [],
  blockConfig,
  openGraph,
  page,
  seo,
}: PageProps) => {
  const { locales, query } = useRouter()
  const { t } = useTranslation()

  const filteredAlternateLinks = useMemo(() => filter(
    alternateLinks,
    (link) => {
      if (locales && locales.includes(link.locale) === false) {
        return false
      }

      return true
    },
  ), [alternateLinks, locales])

  const [pageURL, pageURLExtra, titleExtra] = useMemo(() => {
    const paginationBlockConfig = find(blockConfig, block => block.algolia?.usePagination === true)
    if (!paginationBlockConfig?.algolia?.canonicals) {
      return [page.url || '', '', []]
    }

    const title = []
    const searchParams = new URLSearchParams()

    if (query.page && typeof query.page === 'string') {
      const pageNumber = parseInt(query.page, 10)
      if (pageNumber > 1) {
        title.push(t('PAGINATION_PAGE_HEADER', { count: pageNumber }))
        searchParams.set('page', pageNumber.toString())
      }
    }

    const incomingFilters = Object.keys(query).filter(key => key.startsWith('filter_'))
    if (incomingFilters.length === 1) {
      const filterKey = incomingFilters[0]
      const filterValue = query[filterKey]

      if (filterValue && typeof filterValue === 'string') {
        const canonical = paginationBlockConfig.algolia.canonicals[`${filterKey}=${filterValue}`]
        if (canonical?.name?.defaults) {
          title.push(t(canonical.name.defaults))
          searchParams.set(filterKey, filterValue)
        }
      }
    }

    let urlExtra = ''
    if (searchParams.size !== 0) {
      const sortedSearchParams = new URLSearchParams([...searchParams.entries()].sort())
      urlExtra = `?${sortedSearchParams.toString()}`
    }

    const url = `${page.url || ''}${urlExtra}`

    return [url, urlExtra, title]
  }, [blockConfig, page?.url, query, t])

  return (
    <Head>
      {/* SEO */}
      {seo?.title && <title>{[...titleExtra, seo.title].join(' - ')}</title>}
      {seo?.description && <meta key="description" content={seo.description} name="description" />}
      <meta key="robots" content={seo?.noIndex === true ? 'noindex, follow' : 'index, follow'} name="robots" />
      {pageURL && <link key="canonical" href={pageURL} rel="canonical" />}

      {/* Open Graph */}
      {openGraph?.description && <meta key="og:description" content={openGraph.description} property="og:description" />}
      {openGraph?.image && <meta key="og:image" content={contentstackLoader({ src: openGraph.image, width: 1200 })} property="og:image" />}
      {openGraph?.siteName && <meta key="og:site_name" content={openGraph.siteName} property="og:site_name" />}
      {openGraph?.title && <meta key="og:title" content={[...titleExtra, openGraph.title].join(' - ')} property="og:title" />}
      {openGraph?.type && <meta key="og:type" content={openGraph.type} property="og:type" />}
      {page.url && <meta key="og:url" content={pageURL} property="og:url" />}

      {/* Alternate Links */}
      {map(filteredAlternateLinks, item => (
        <link
          key={item.locale}
          href={`${item.url || ''}${pageURLExtra}`}
          hrefLang={item.locale}
          rel="alternate"
        />
      ))}
    </Head>
  )
}

export const PageHOC = <T extends PageProps>(Component: NextPage<T>): NextPage<T> => (props) => {
  const {
    blockConfig,
    page,
    swrFallback,
  } = props

  return (
    <>
      <DynamicHead {...props} />

      {/* Render component */}
      <SWRConfig value={{ fallback: swrFallback }}>
        <BlockConfigContext.Provider value={blockConfig}>
          <Component {...props} />
        </BlockConfigContext.Provider>
      </SWRConfig>

      {/* Tracking */}
      <DataLayer input={{ contentIds: page.id, pageType: page.type, type: page.type }} />
    </>
  )
}

